import { averageScore } from '@/lib/scoreStatistics';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, UserPlus, TrendingUp, Users, Award, Activity, Eye } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { QuickStatsCard } from '@/components/dashboard/QuickStatsCard';
import { SimulationCard } from '@/components/dashboard/SimulationCard';
import { TeamMemberDetailModal } from '@/components/admin/TeamMemberDetailModal';
import { InviteTeamMemberModal } from '@/components/admin/InviteTeamMemberModal';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { SkillGapAnalysis } from '@/components/admin/SkillGapAnalysis';
import { UsageHeatmap } from '@/components/admin/UsageHeatmap';
import { GoalTrackingWidget } from '@/components/admin/GoalTrackingWidget';
import { AgentPerformanceTrends } from '@/components/admin/AgentPerformanceTrends';
import { LeaderboardPreview } from '@/components/admin/LeaderboardPreview';
import { TeamReportExport } from '@/components/admin/TeamReportExport';
import { PendingInvitesSection } from '@/components/admin/PendingInvitesSection';
import { AgentAssignmentManager } from '@/components/admin/AgentAssignmentManager';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  sessions: number;
  bestScore: number | null;
  avgScore: number | null;
  lastSession: string;
}

export function CompanyDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Fetch company info including tenant_id
  // Use user.company_id (tenant-scoped via tenant_memberships), not profile.company_id
  // (profile.company_id is a legacy single column that cannot represent per-tenant context)
  useEffect(() => {
    async function fetchCompanyInfo() {
      if (!user?.company_id) return;

      const { data: company } = await supabase
        .from('companies')
        .select('name, tenant_id')
        .eq('id', user.company_id)
        .maybeSingle();

      setCompanyId(user.company_id);
      setCompanyName(company?.name || 'Company');
      setTenantId(company?.tenant_id || null);
    }

    fetchCompanyInfo();
  }, [user]);

  // Fetch team members from profiles (all users in company) + performance data
  const { data: teamMembers = [], refetch: refetchTeam } = useQuery({
    queryKey: ['team-members', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      // Fetch all profiles in this company
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, display_name, email')
        .eq('company_id', companyId);
      
      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        return [];
      }
      
      // Fetch performance data from cache
      const { data: performanceData } = await supabase
        .from('team_performance_cache')
        .select('*')
        .eq('company_id', companyId);
      
      // Create a map of performance data by user_id
      const performanceMap = new Map(
        (performanceData || []).map(p => [p.user_id, p])
      );
      
      // Merge profiles with performance data
      return (profiles || []).map(profile => {
        const perf = performanceMap.get(profile.id);
        return {
          id: profile.id,
          name: profile.display_name || 'Unknown',
          email: profile.email || '',
          sessions: perf?.total_sessions || 0,
          bestScore: perf?.best_score ?? null,
          avgScore: perf?.average_score == null ? null : Math.round(perf.average_score),
          lastSession: perf?.last_session_date 
            ? new Date(perf.last_session_date).toLocaleDateString()
            : 'Never',
        };
      }).sort((a, b) => (b.avgScore ?? -1) - (a.avgScore ?? -1)) as TeamMember[];
    },
    enabled: !!companyId,
  });

  // Fetch company agents (tenant-specific + system agents)
  const { data: companyAgents = [] } = useQuery({
    queryKey: ['company-agents', companyId, tenantId],
    queryFn: async () => {
      if (!companyId) return [];
      
      // Build query for agents available to this company:
      // 1. Agents assigned to this tenant (tenant-specific)
      // 2. System agents (available to all tenants)
      // 3. Agents directly assigned to this company
      let query = supabase
        .from('simulation_agents')
        .select('*')
        .eq('status', 'active');

      if (tenantId) {
        // Include: tenant agents + system agents + company-specific agents
        query = query.or(`tenant_id.eq.${tenantId},is_system.eq.true,company_id.eq.${companyId}`);
      } else {
        // Fallback: just company-specific agents + system agents
        query = query.or(`company_id.eq.${companyId},is_system.eq.true`);
      }

      const { data } = await query.order('created_at', { ascending: false });
      
      return data || [];
    },
    enabled: !!companyId,
  });

  // Fetch real chart data - daily averages for the last 30 days
  const { data: chartData = [] } = useQuery({
    queryKey: ['company-progress-chart', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data } = await supabase
        .from('simulation_evaluations')
        .select('overall_score, evaluated_at')
        .eq('company_id', companyId)
        .eq('is_submitted', true)
        .is('deleted_at', null)
        .gte('evaluated_at', thirtyDaysAgo.toISOString())
        .not('overall_score', 'is', null)
        .order('evaluated_at', { ascending: true });
      
      if (!data || data.length === 0) return [];
      
      // Group by day and calculate averages
      const dailyScores = new Map<string, { total: number; count: number }>();
      
      data.forEach((evaluation) => {
        const date = new Date(evaluation.evaluated_at).toISOString().split('T')[0];
        const existing = dailyScores.get(date) || { total: 0, count: 0 };
        dailyScores.set(date, {
          total: existing.total + (evaluation.overall_score || 0),
          count: existing.count + 1,
        });
      });
      
      // Convert to array with day numbers
      const results: { day: number; date: string; score: number }[] = [];
      const today = new Date();
      
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayData = dailyScores.get(dateStr);
        
        results.push({
          day: 30 - i,
          date: dateStr,
          score: dayData ? Math.round(dayData.total / dayData.count) : 0,
        });
      }
      
      return results;
    },
    enabled: !!companyId,
  });

  // Activity counts include unscored sessions; performance does not.
  const { data: scoredTeamAverage = null } = useQuery({
    queryKey: ['company-scored-average', companyId, tenantId],
    enabled: !!companyId && !!tenantId,
    queryFn: async () => {
      const scoredRows: { overall_score: number | null }[] = [];
      const pageSize = 1000;
      for (let offset = 0; ; offset += pageSize) {
        const { data, error } = await supabase.from('simulation_evaluations')
          .select('overall_score').eq('company_id', companyId!).eq('tenant_id', tenantId!)
          .is('deleted_at', null).not('overall_score', 'is', null)
          .order('id').range(offset, offset + pageSize - 1);
        if (error) throw error;
        scoredRows.push(...(data ?? []));
        if (!data || data.length < pageSize) break;
      }
      return averageScore(scoredRows);
    },
  });
  
  const stats = {
    teamMembers: teamMembers.length,
    completedSessions: teamMembers.reduce((sum, m) => sum + m.sessions, 0),
    avgTeamScore: scoredTeamAverage,
    activeAgents: companyAgents.length,
  };

  const handleExportCSV = () => {
    if (teamMembers.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Name', 'Email', 'Sessions', 'Best Score', 'Avg Score', 'Last Session'];
    const csvContent = [
      headers.join(','),
      ...teamMembers.map(m => 
        [m.name, m.email, m.sessions, m.bestScore ?? 'Not scored', m.avgScore ?? 'Not scored', m.lastSession].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${companyName}-team-performance-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  if (!companyId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">You are not associated with a company.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-8 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-foreground mb-2">Company Dashboard</h1>
            <p className="text-muted-foreground text-lg">{companyName} - Admin View</p>
          </div>
          <div className="flex items-center gap-3">
            <TeamReportExport companyId={companyId} companyName={companyName} />
            <Button onClick={() => setShowInviteModal(true)} className="gap-2">
              <UserPlus className="h-4 w-4" />
              Invite Team Member
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <QuickStatsCard
            icon={Users}
            title="Team Members"
            value={stats.teamMembers.toString()}
            subtitle="Active users"
          />
          <QuickStatsCard
            icon={Activity}
            title="Completed Sessions"
            value={stats.completedSessions.toString()}
            subtitle="Total team completions"
          />
          <QuickStatsCard
            icon={Award}
            title="Avg Team Score"
            value={stats.avgTeamScore === null ? '—' : `${stats.avgTeamScore}%`}
            subtitle="Overall performance"
            trend="+5%"
            trendType="positive"
          />
          <QuickStatsCard
            icon={TrendingUp}
            title="Active Agents"
            value={stats.activeAgents.toString()}
            subtitle="Available simulations"
          />
        </div>

        {/* Team Performance Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Performance
              </CardTitle>
              <CardDescription>Click on a team member to view detailed progress</CardDescription>
            </div>
            <Button onClick={handleExportCSV} variant="outline" size="sm" className="gap-2">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </CardHeader>
          <CardContent>
            {teamMembers.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No team performance data yet</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-center">Sessions</TableHead>
                      <TableHead className="text-center">Best Score</TableHead>
                      <TableHead className="text-center">Avg Score</TableHead>
                      <TableHead>Last Session</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teamMembers.map((member) => (
                      <TableRow 
                        key={member.id} 
                        className="hover:bg-muted/50 cursor-pointer"
                        onClick={() => setSelectedMember(member)}
                      >
                        <TableCell className="font-medium">{member.name}</TableCell>
                        <TableCell className="text-muted-foreground">{member.email}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{member.sessions}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={member.bestScore >= 80 ? "default" : "outline"}>
                            {member.bestScore ?? '—'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={member.avgScore >= 70 ? "default" : "outline"}>
                            {member.avgScore ?? '—'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{member.lastSession}</TableCell>
                        <TableCell>
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Company Agents */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                🎯 Company Agents
              </CardTitle>
              <CardDescription>Simulations assigned to or available for your company</CardDescription>
            </div>
            <Button 
              onClick={() => navigate('/admin/agents/create')}
              size="sm"
              className="gap-2"
            >
              <UserPlus className="h-4 w-4" />
              Create Agent
            </Button>
          </CardHeader>
          <CardContent>
            {companyAgents.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No agents available</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {companyAgents.slice(0, 6).map((agent) => (
                  <SimulationCard
                    key={agent.id}
                    agentName={agent.name}
                    title={agent.title || ''}
                    specialty={agent.specialty || ''}
                    experience={agent.experience ? `${agent.experience} years` : 'Experienced'}
                    goalScore={agent.goal_score || 85}
                    yourBestScore={undefined}
                    imageUrl={agent.image_url || undefined}
                    platform={agent.platform as 'elevenlabs' | 'anam' | undefined}
                    onStart={() => navigate(`/simulation/${agent.id}`)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Progress Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              📊 Team Progress Over Time
            </CardTitle>
            <CardDescription>Average team performance over the last 30 days</CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.length === 0 || chartData.every(d => d.score === 0) ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No performance data available yet
              </div>
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="day" 
                      label={{ value: 'Days', position: 'insideBottom', offset: -5 }}
                      className="text-xs"
                    />
                    <YAxis 
                      label={{ value: 'Score %', angle: -90, position: 'insideLeft' }}
                      domain={[0, 100]}
                      className="text-xs"
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value: number, name: string, props: any) => [
                        `${value}%`,
                        props.payload.date || 'Score'
                      ]}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--primary))', r: 3 }}
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Skill Gap & Usage Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SkillGapAnalysis companyId={companyId} />
          <UsageHeatmap companyId={companyId} />
        </div>

        {/* Goal Tracking & Performance Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GoalTrackingWidget companyId={companyId} />
          <AgentPerformanceTrends companyId={companyId} />
        </div>

        {/* Pending Invitations */}
        <PendingInvitesSection companyId={companyId} />

        {/* Agent Assignments */}
        <AgentAssignmentManager companyId={companyId} />

        {/* Leaderboard Preview */}
        <LeaderboardPreview companyId={companyId} />
      </div>

      {/* Team Member Detail Modal */}
      {selectedMember && (
        <TeamMemberDetailModal
          open={!!selectedMember}
          onClose={() => setSelectedMember(null)}
          memberId={selectedMember.id}
          memberName={selectedMember.name}
          memberEmail={selectedMember.email}
        />
      )}

      {/* Invite Team Member Modal */}
      <InviteTeamMemberModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        companyId={companyId}
        companyName={companyName}
        onInviteSent={() => refetchTeam()}
      />
    </div>
  );
}
